import { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button/standard-button';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import InlineEdit from '@atlaskit/inline-edit';
import { Box, Flex, Inline, Text, xcss } from '@atlaskit/primitives';
import { CopyButton } from '@atlassian/bitkit-copy';

import { useStatsigGate } from 'src/contexts/feature-provider';
import { StatsigFeatureKeys } from 'src/features';
import { useCheckUserAccess } from 'src/hooks/use-check-user-access';
import { getCurrentPullRequestSourceBranchDetails } from 'src/redux/pull-request/selectors';
import { ClientLinkWrapper } from 'src/router/components';
import { Link } from 'src/router/components/link';
import { Ref } from 'src/sections/create-branch';
import { REF_TYPE } from 'src/sections/create-branch/constants';
import { getRef } from 'src/sections/create-branch/utils';
import { BranchSelector } from 'src/sections/repository/components/ref-selector/branch-selector';
import { useRefFetchActions } from 'src/sections/repository/sections/branches/hooks/use-ref-fetch-actions';
import { useRetargetPullRequest } from 'src/sections/repository/sections/pull-request/hooks/retarget-pr';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { messages } from './branch-info.i18n';
import * as infoStyles from './branch-info.styled';

type Props = {
  branchHref?: string;
  branchName: string;
  repoAvatarSrc?: string;
  repoHref?: string;
  repoName?: string;
  repoOwnerAvatarSrc?: string;
  repoOwnerHref?: string;
  repoOwnerName?: string;
  supportsRetargeting?: boolean;
  closeBranchInfo?: () => void;
};

const branchNameText = xcss({
  maxWidth: '360px',
});

const formerUserContainerStyles = xcss({
  paddingBlock: 'space.050',
  paddingInline: 'space.100',
});

export const BranchInfo = ({
  branchHref,
  branchName,
  repoAvatarSrc,
  repoHref,
  repoName,
  repoOwnerAvatarSrc,
  repoOwnerHref,
  repoOwnerName,
  supportsRetargeting = false,
  closeBranchInfo,
}: Props) => {
  const intl = useIntl();
  const hasSourceBranchDetails = useSelector(
    getCurrentPullRequestSourceBranchDetails
  );
  const userHasWrite = useCheckUserAccess('write');

  const [destination, setDestination] = useState<Ref>({
    name: branchName,
    type: REF_TYPE.BRANCH,
  });

  const setDestinationWrapper = (branch: Ref | undefined) => {
    if (branch) {
      setDestination(branch);
    }
  };

  const { retargetPullRequest } = useRetargetPullRequest();

  const { fetchRefs, fetchNextRefs, state } = useRefFetchActions({
    sourceBranch: hasSourceBranchDetails?.name,
  });

  const copyableBranchName = useStatsigGate(
    StatsigFeatureKeys.copyableBranchName
  );

  useEffect(() => {
    fetchRefs();
  }, [destination, fetchRefs]);

  const publishBranchLinkClick = (linkId: string) => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: linkId,
      source: 'branchInfo',
    });
  };

  const publishRetargetClick = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'retarget',
      source: 'prView',
    });
  };

  const renderBranchInfoListElement = (params: {
    leftColumnContent: string;
    rightColumnContent: JSX.Element;
  }) => {
    return (
      <infoStyles.BranchInfoListItem>
        <infoStyles.LeftColumn aria-hidden="true">
          {params.leftColumnContent}
        </infoStyles.LeftColumn>
        <infoStyles.RightColumn>
          {params.rightColumnContent}
        </infoStyles.RightColumn>
      </infoStyles.BranchInfoListItem>
    );
  };

  const renderRepositoryOwner = () => {
    if (!repoOwnerName) {
      return null;
    }

    const repoOwnerAvatar = (
      <Avatar name={repoOwnerName} size="small" src={repoOwnerAvatarSrc} />
    );

    const content = {
      leftColumnContent: intl.formatMessage(messages.repoWorkspaceLabel),
      rightColumnContent: repoOwnerHref ? (
        <ClientLinkWrapper
          url={`${repoOwnerHref.replace(/\/$/, '')}/workspace/overview/`}
        >
          <Button
            appearance="link"
            href={repoOwnerHref}
            onClick={_ => publishBranchLinkClick('repoOwnerHref')}
            // Accessibility bugfix: Anchor links should not have type="button"
            // @ts-ignore -> TODO remove when @atlaskit/button is updated
            type={null}
            iconBefore={repoOwnerAvatar}
            aria-label={intl.formatMessage(messages.openLinkLabel, {
              name: repoOwnerName,
              type: 'workspace',
            })}
          >
            {repoOwnerName}
          </Button>
        </ClientLinkWrapper>
      ) : (
        <Inline
          space="space.050"
          alignBlock="center"
          xcss={formerUserContainerStyles}
        >
          {repoOwnerAvatar}
          <Text>{repoOwnerName}</Text>
        </Inline>
      ),
    };
    return renderBranchInfoListElement(content);
  };

  const renderRepositoryName = () => {
    const content = {
      leftColumnContent: intl.formatMessage(messages.repoNameLabel),
      rightColumnContent: (
        <ClientLinkWrapper url={repoHref}>
          <Button
            appearance="link"
            isDisabled={!repoName}
            href={repoHref}
            onClick={_ => publishBranchLinkClick('repoHref')}
            // Accessibility bugfix: Anchor links should not have type="button"
            // @ts-ignore -> TODO remove when @atlaskit/button is updated
            type={null}
            iconBefore={
              <Avatar name={repoName} src={repoAvatarSrc} size="small" />
            }
            aria-label={
              repoName
                ? intl.formatMessage(messages.openLinkLabel, {
                    name: repoName,
                    type: 'repository',
                  })
                : undefined
            }
          >
            {repoName || intl.formatMessage(messages.deletedRepository)}
          </Button>
        </ClientLinkWrapper>
      ),
    };
    return renderBranchInfoListElement(content);
  };

  const onCancel = () => {
    const branchRef = getRef({ name: branchName });
    setDestination(branchRef);
  };

  const onConfirm = () => {
    // No need to retarget if the user chooses the same branch as the existing destination
    if (destination.name === branchName) {
      return;
    }
    publishRetargetClick();
    if (closeBranchInfo) {
      closeBranchInfo();
    }
    retargetPullRequest(destination.name);
  };

  const renderBranchReadView = () => {
    return (
      <infoStyles.ReadContainer>
        <infoStyles.LinkContainer>
          <Link
            to={branchHref || ''}
            onClick={() => publishBranchLinkClick('branchHref')}
            aria-label={intl.formatMessage(messages.openLinkLabel, {
              name: branchName,
              type: 'branch',
            })}
            data-testid="branch-info-read"
          >
            <infoStyles.LinkTextContainer>
              <infoStyles.LinkTextSpan>{branchName}</infoStyles.LinkTextSpan>
            </infoStyles.LinkTextContainer>
          </Link>
        </infoStyles.LinkContainer>
        <infoStyles.Icon>
          <ChevronDownIcon label="" spacing="spacious" size="small" />
        </infoStyles.Icon>
      </infoStyles.ReadContainer>
    );
  };

  const renderBranchEditView = () => {
    return (
      <infoStyles.EditContainer>
        <BranchSelector
          branchSelector={state}
          value={{ label: destination.name, value: destination.name }}
          onChangeBranch={setDestinationWrapper}
          fetchRefOptions={fetchRefs}
          fetchNextRefOptions={fetchNextRefs}
          autoFocus
          startMenuOpen
        />
      </infoStyles.EditContainer>
    );
  };

  const renderBranchName = () => {
    const allowRetargeting =
      !!supportsRetargeting && userHasWrite && !!hasSourceBranchDetails;

    const branchButton = (
      <ClientLinkWrapper url={branchHref}>
        <Button
          appearance="link"
          href={branchHref}
          onClick={_ => publishBranchLinkClick('branchHref')}
          // Accessibility bugfix: Anchor links should not have type="button"
          // @ts-ignore -> TODO remove when @atlaskit/button is updated
          type={null}
          isDisabled={!branchHref}
          aria-label={intl.formatMessage(messages.openLinkLabel, {
            name: branchName,
            type: 'branch',
          })}
        >
          {branchName}
        </Button>
      </ClientLinkWrapper>
    );

    const content = {
      leftColumnContent: intl.formatMessage(messages.branchLabel),
      rightColumnContent: allowRetargeting ? (
        <infoStyles.RetargetBranch>
          <InlineEdit
            defaultValue={branchName}
            readViewFitContainerWidth
            keepEditViewOpenOnBlur
            editView={renderBranchEditView}
            readView={renderBranchReadView}
            onConfirm={onConfirm}
            onCancel={onCancel}
          />
        </infoStyles.RetargetBranch>
      ) : (
        <>
          {copyableBranchName ? (
            <Flex gap="space.100" alignItems="center">
              {' '}
              <Box xcss={branchNameText}>{branchButton}</Box>
              <CopyButton value={branchName} />
            </Flex>
          ) : (
            branchButton
          )}
        </>
      ),
    };
    return renderBranchInfoListElement(content);
  };

  return (
    <infoStyles.BranchInfoList role="list">
      {renderRepositoryOwner()}
      {renderRepositoryName()}
      {renderBranchName()}
    </infoStyles.BranchInfoList>
  );
};
